import { cn } from "../../../../lib/utils";

/**
 * Loading placeholders for the claim details page.
 *
 * The claim header and the claim history come from two different calls, so the
 * page fills in twice. That is the whole reason these are shaped like the real
 * thing rather than being a spinner: every block below matches the geometry of
 * the component that replaces it — same corner radius, same padding, same row
 * heights, same 380px rail — so nothing shifts when the data lands.
 *
 * Keep them in step. A skeleton that no longer matches its component is worse
 * than none at all: it promises one layout and then delivers another.
 */
// cn, not string concatenation: a caller's rounded-full / rounded-xl has to
// replace the default rounded-md, not sit beside it and lose on CSS order
// (the stage dots were drawing as rounded squares).
const Shim = ({ className = "" }) => (
  <div className={cn("claim-details-skeleton-shimmer rounded-md", className)} />
);

const SkeletonStyles = () => (
  <style>{`
    .claim-details-skeleton-shimmer {
      background: linear-gradient(
        90deg,
        #E6EBEE 0%,
        #F2F5F7 50%,
        #E6EBEE 100%
      );
      background-size: 200% 100%;
      animation: claimDetailsShimmer 1.4s ease-in-out infinite;
    }
    @keyframes claimDetailsShimmer {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    [dir="rtl"] .claim-details-skeleton-shimmer { animation-direction: reverse; }
    @media (prefers-reduced-motion: reduce) {
      .claim-details-skeleton-shimmer { animation: none; }
    }
  `}</style>
);

/** Mirrors ClaimListCard: pill, title, timestamp, trailing chevron. */
const CardShim = () => (
  <div className="flex items-center gap-3 rounded-xl border border-[#DDE2E4] bg-white p-4">
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <Shim className="h-[22px] w-24 rounded-md" />
      <Shim className="h-[22px] w-40" />
      <Shim className="h-4 w-24" />
    </div>
    <Shim className="size-4 shrink-0" />
  </div>
);

/** Mirrors ClaimSection: heading plus its cards. */
const SectionShim = ({ rows, showHeading }) => (
  <div className="flex flex-col gap-3">
    {showHeading && <Shim className="h-6 w-28" />}
    {Array.from({ length: rows }).map((_, row) => (
      <CardShim key={row} />
    ))}
  </div>
);

/**
 * Mirrors ClaimTimeline, including its highlighted newest entry — the tinted
 * card is held while loading so the highlight does not appear out of nowhere
 * when the data lands.
 */
const TimelineShim = ({ showHeading }) => (
  <div className="flex flex-col gap-4">
    {showHeading && <Shim className="h-6 w-36" />}
    {/* Connectors drawn per row, matching ClaimTimeline — a single `border-s`
        on the container would hang a stub of line above the first circle. */}
    <div className="relative ms-2">
      {[0, 1, 2].map((row) => {
        const isLatest = row === 0;
        const dotCentre = isLatest ? 22 : 10;
        return (
          <div key={row} className="relative ps-6 pb-7">
            {row < 2 && (
              <span
                aria-hidden="true"
                className="absolute start-0 w-px -translate-x-1/2 bg-[#DDE2E4] rtl:translate-x-1/2"
                style={{ top: dotCentre, bottom: -10 }}
              />
            )}
            <span
              aria-hidden="true"
              className={cn(
                "absolute start-0 -translate-x-1/2 rounded-full bg-[#E6EBEE] ring-white rtl:translate-x-1/2",
                isLatest ? "size-3.5 ring-[5px]" : "size-2.5 ring-4"
              )}
              style={{ top: dotCentre - (isLatest ? 7 : 5) }}
            />
            <div
              className={cn(
                isLatest &&
                  "rounded-xl border border-[#B3E2F6] bg-[#F2FAFD] p-3.5"
              )}
            >
              <div className="flex items-center justify-between gap-3">
                {isLatest && <Shim className="h-4 w-24" />}
                <Shim className="h-4 w-28" />
              </div>
              <Shim className="mt-2 h-4 w-full" />
              <Shim className="mt-1.5 h-4 w-4/5" />
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/**
 * The claim identity card and the stage tracker — the two blocks that arrive
 * together from the claim details call.
 */
export const ClaimHeaderSkeleton = () => (
  <>
    <SkeletonStyles />
    <div className="w-full">
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-[#DDE2E4] bg-[#F6F8F9] p-4 sm:p-5">
        <div className="min-w-0">
          <Shim className="h-4 w-20" />
          <Shim className="mt-0.5 h-7 w-52" />
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
            {/* The status pill keeps its own pill radius so it does not read as
                another line of text while loading. */}
            <Shim className="h-5 w-24 rounded-[10px]" />
            <Shim className="h-4 w-28" />
            <Shim className="h-4 w-24" />
          </div>
        </div>
        <Shim className="h-[42px] w-36 rounded-xl" />
      </div>

      <div className="mt-4 rounded-2xl border border-[#DDE2E4] bg-white p-4 sm:p-5">
        {/* The phone-only stage line ClaimsStepper puts above the dots: the
            current stage (16/24) and "Stage n of 5 · Next: …" (13/18). */}
        <div className="mb-4 hidden max767:block">
          <Shim className="h-6 w-28" />
          <Shim className="mt-0.5 h-[18px] w-48" />
        </div>
        {/* Five stages with their connectors, laid out exactly as ClaimsStepper
            does — flex-1 columns with a 92px floor, no floor and no labels on a
            phone — so nothing moves when the real stepper replaces this. */}
        <div className="flex w-full">
          {[0, 1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className="flex min-w-[92px] flex-1 flex-col items-center gap-2 max767:min-w-0"
            >
              <div className="flex w-full items-center">
                <span
                  className={cn(
                    "h-0.5 flex-1 rounded-full bg-[#E6EBEE]",
                    step === 0 && "invisible"
                  )}
                />
                <Shim className="size-5 shrink-0 rounded-full" />
                <span
                  className={cn(
                    "h-0.5 flex-1 rounded-full bg-[#E6EBEE]",
                    step === 4 && "invisible"
                  )}
                />
              </div>
              <Shim className="h-4 w-14 max767:hidden" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </>
);

/**
 * The claim history call fills four sections, and the page arranges them two
 * different ways — so this has to as well, or the skeleton would promise a
 * layout the data does not arrive into.
 *
 * `mobile` here means "not the desktop two-column layout", matching the
 * `!desktop` the page passes: below 1024px the sections are tabbed, so the
 * placeholder shows a tab strip and the first panel rather than a stack of
 * every section at once.
 */
export const ClaimBodySkeleton = ({ mobile = false }) => (
  <>
    <SkeletonStyles />

    {mobile ? (
      <div>
        {/* Mirrors ClaimSectionTabs: 44px row, divider drawn as an inset
            shadow, tabs 12px apart with the first one's underline held. */}
        <div className="-mx-1 flex gap-1 overflow-hidden px-1 shadow-[inset_0_-1px_0_#DDE2E4]">
          {["w-[95px]", "w-[87px]", "w-[96px]", "w-[63px]"].map((width, tab) => (
            <div key={tab} className="relative shrink-0 px-3 py-3">
              <Shim className={cn("h-5", width)} />
              {tab === 0 && (
                <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-[#E6EBEE]" />
              )}
            </div>
          ))}
        </div>
        <div className="pt-5">
          {/* "Claim progress" is the tab the page opens on. */}
          <TimelineShim showHeading={false} />
        </div>
      </div>
    ) : (
      <div className="grid grid-cols-[minmax(0,1fr)_380px] items-start gap-8">
        <TimelineShim showHeading />
        <div className="flex flex-col gap-6">
          <SectionShim rows={2} showHeading />
          <SectionShim rows={3} showHeading />
          <SectionShim rows={1} showHeading />
        </div>
      </div>
    )}
  </>
);

const ClaimDetailsSkeleton = ({ mobile = false }) => (
  <div className="w-[1200px] max-w-full p-4 sm:p-6">
    <ClaimHeaderSkeleton />
    <div className="mt-6">
      <ClaimBodySkeleton mobile={mobile} />
    </div>
  </div>
);

export default ClaimDetailsSkeleton;
